import { escapeHtml } from "./search.js";

export function createTranscriptSearch({
  document,
  window,
  transcriptElements,
  getCurrentTranscriptContent,
  setCurrentTranscriptContent,
  getCurrentTranscriptFilename,
  getTranscriptScrollbar,
  getSearchMode,
  setSearchMode,
  getSearchTerm,
  contentHits,
  contentSearchMinLength,
  filterFiles,
  viewTranscript,
  maybeQueueInDocSearchPreset,
  libraryViewElement,
}) {
  let query = "";
  let matches = [];
  let activeIndex = -1;

  function clear() {
    query = "";
    matches = [];
    activeIndex = -1;
    if (transcriptElements && transcriptElements.content) {
      const text = getCurrentTranscriptContent() || "";
      transcriptElements.content.textContent = text;
    }
    try {
      document.dispatchEvent(
        new CustomEvent("demo:transcript-ready", {
          detail: {
            filename: getCurrentTranscriptFilename(),
            length: (getCurrentTranscriptContent() || "").length,
          },
        }),
      );
    } catch (_) {}
    updateSearchCounter();
  }

  function applyHighlights() {
    const el = transcriptElements && transcriptElements.content;
    if (!el) return;
    if (!query || matches.length === 0) {
      el.textContent = getCurrentTranscriptContent() || "";
      return;
    }
    const text = getCurrentTranscriptContent() || "";
    let html = "";
    let last = 0;
    for (let i = 0; i < matches.length; i += 1) {
      const m = matches[i];
      html += escapeHtml(text.slice(last, m.start));
      const cls =
        i === activeIndex ? "indoc-highlight active" : "indoc-highlight";
      html +=
        '<mark class="' +
        cls +
        '">' +
        escapeHtml(text.slice(m.start, m.end)) +
        "</mark>";
      last = m.end;
    }
    html += escapeHtml(text.slice(last));
    el.innerHTML = html;
  }

  function scrollToActive() {
    const container = transcriptElements && transcriptElements.body;
    const contentEl = transcriptElements && transcriptElements.content;
    if (!container || !contentEl) return;
    const marks = contentEl.querySelectorAll("mark.indoc-highlight");
    if (!marks || marks.length === 0) return;
    const target = marks[Math.max(0, Math.min(activeIndex, marks.length - 1))];
    if (!target) return;
    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const delta =
      targetRect.top - containerRect.top - container.clientHeight * 0.3;
    container.scrollTop += delta;
    const transcriptScrollbar = getTranscriptScrollbar();
    if (transcriptScrollbar) transcriptScrollbar.refresh();
  }

  function buildMatches(term) {
    const contentElement = transcriptElements && transcriptElements.content;
    if (!contentElement) return [];
    let text = getCurrentTranscriptContent() || "";
    if (!text) {
      text = contentElement.textContent || "";
      if (text) setCurrentTranscriptContent(text);
    }
    const needle = (term || "").trim();
    if (
      window.__SIFT_DEMO_TEST_CONFIG &&
      window.__SIFT_DEMO_TEST_CONFIG.debug
    ) {
      try {
        // eslint-disable-next-line no-console
        console.log("[demo debug] buildMatches start", {
          query: needle,
          contentLength: text.length,
        });
      } catch (_) {}
    }
    if (!needle) {
      clear();
      return [];
    }
    const lowerText = text.toLowerCase();
    const lowerNeedle = needle.toLowerCase();
    const found = [];
    let pos = 0;
    while (true) {
      const idx = lowerText.indexOf(lowerNeedle, pos);
      if (idx === -1) break;
      found.push({ start: idx, end: idx + needle.length });
      pos = idx + needle.length;
      if (found.length > 1000) break;
    }
    query = needle;
    matches = found;
    activeIndex = found.length > 0 ? 0 : -1;
    applyHighlights();
    updateSearchCounter();
    if (found.length > 0) scrollToActive();
    if (
      window.__SIFT_DEMO_TEST_CONFIG &&
      window.__SIFT_DEMO_TEST_CONFIG.debug
    ) {
      try {
        // eslint-disable-next-line no-console
        console.log("[demo debug] buildMatches result", {
          query: needle,
          matches: found.length,
          activeIndex,
          highlightInnerHTMLLength:
            contentElement && typeof contentElement.innerHTML === "string"
              ? contentElement.innerHTML.length
              : null,
        });
      } catch (_) {}
    }
    return found;
  }

  function next() {
    if (!matches.length) return;
    activeIndex = (activeIndex + 1) % matches.length;
    applyHighlights();
    updateSearchCounter();
    scrollToActive();
  }

  function prev() {
    if (!matches.length) return;
    activeIndex = (activeIndex - 1 + matches.length) % matches.length;
    applyHighlights();
    updateSearchCounter();
    scrollToActive();
  }

  function hasQuery() {
    return !!query && matches.length > 0;
  }

  function getMatchCount() {
    return matches.length;
  }

  function getActiveIndex() {
    return activeIndex;
  }

  function updateSearchCounter() {
    const counter = document.querySelector(".indoc-search-counter");
    if (!counter) return;

    const count = matches.length;
    const activeIdx = activeIndex;
    const hasQuery = !!query;

    if (!hasQuery) {
      counter.style.display = "none";
      counter.textContent = "";
      counter.style.opacity = "";
      return;
    }

    counter.style.display = "block";

    if (count === 0) {
      counter.textContent = "no matches";
      counter.style.opacity = "0.5";
    } else {
      counter.textContent = `${activeIdx + 1}/${count}`;
      counter.style.opacity = "1";
    }
  }

  const InDocSearch = {
    clear,
    buildMatches,
    next,
    prev,
    hasQuery,
    getMatchCount,
    getActiveIndex,
  };

  const SearchHotkeys = (function () {
    let inDocBar = null;
    let inDocInput = null;
    let inDocCounter = null;
    function getLibraryInput() {
      return document.querySelector(".library-controls .search-input");
    }

    function getLibraryChips() {
      return document.querySelector(".library-controls .search-modes");
    }

    function isTypingTarget(el) {
      return (
        el &&
        (el.tagName === "INPUT" ||
          el.tagName === "TEXTAREA" ||
          el.isContentEditable)
      );
    }

    function isLibraryActive() {
      return !!(
        libraryViewElement && libraryViewElement.classList.contains("active")
      );
    }

    function isTranscriptActive() {
      return document.body.classList.contains("showing-transcript");
    }

    function focusLibrarySearch(selectAll = true) {
      const input = getLibraryInput();
      if (!input) return;
      input.focus();
      if (selectAll) input.select();
    }

    function cycleMode(backward = false) {
      const modes = ["files", "content"];
      const current = getSearchMode() === "content" ? "content" : "files";
      const idx = modes.indexOf(current);
      const nextMode = backward
        ? modes[(idx - 1 + modes.length) % modes.length]
        : modes[(idx + 1) % modes.length];
      setSearchMode(nextMode);
    }

    function openFirstVisibleFileRow() {
      const row = document.querySelector("#fileList .file-row[data-filename]");
      if (!row) return;
      const filename = row.getAttribute("data-filename");
      if (filename) {
        maybeQueueInDocSearchPreset(filename);
        viewTranscript(filename);
      }
    }

    function ensureInDocBar() {
      if (inDocBar && inDocInput) return;
      const body = document.querySelector(".transcript-view__body");
      const viewport = body && body.querySelector(".custom-scroll__viewport");
      const content = viewport && viewport.querySelector("#transcriptContent");
      if (
        window.__SIFT_DEMO_TEST_CONFIG &&
        window.__SIFT_DEMO_TEST_CONFIG.debug &&
        (!body || !viewport || !content)
      ) {
        try {
          // eslint-disable-next-line no-console
          console.log("[demo debug] ensureInDocBar missing nodes", {
            body: !!body,
            viewport: !!viewport,
            content: !!content,
          });
        } catch (_) {}
      }
      if (!body || !viewport || !content) return;
      inDocBar = document.createElement("div");
      inDocBar.className = "indoc-search-bar";
      inDocBar.setAttribute("role", "dialog");

      inDocInput = document.createElement("input");
      inDocInput.type = "text";
      inDocInput.className = "search-input";
      inDocInput.placeholder = "search in this transcript… (press / to focus)";
      inDocInput.addEventListener("input", () => {
        InDocSearch.buildMatches(inDocInput.value.trim());
      });
      inDocInput.addEventListener("keydown", (e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          hideInDocBar();
          return;
        }
        if (e.key === "Enter") {
          e.preventDefault();
          InDocSearch.next();
          return;
        }
        if (e.key === "Tab") {
          return;
        }
      });

      inDocCounter = document.createElement("div");
      inDocCounter.className = "indoc-search-counter";
      inDocCounter.textContent = "";

      inDocBar.appendChild(inDocInput);
      inDocBar.appendChild(inDocCounter);

      body.insertBefore(inDocBar, viewport);
      try {
        document.dispatchEvent(new CustomEvent("demo:indoc-search-ready"));
      } catch (_) {}
    }

    function showInDocBar(preset, options = {}) {
      ensureInDocBar();
      // add class to body for viewport padding
      const body = document.querySelector(".transcript-view__body");
      if (body) body.classList.add("indoc-active");
      if (
        window.__SIFT_DEMO_TEST_CONFIG &&
        window.__SIFT_DEMO_TEST_CONFIG.debug
      ) {
        try {
          // eslint-disable-next-line no-console
          console.log("[demo debug] showInDocBar invoked", {
            preset,
            hasBar: !!inDocBar,
            hasInput: !!inDocInput,
          });
        } catch (_) {}
      }
      if (!inDocBar || !inDocInput) {
        if (
          window.__SIFT_DEMO_TEST_CONFIG &&
          window.__SIFT_DEMO_TEST_CONFIG.debug
        ) {
          try {
            // eslint-disable-next-line no-console
            console.log("[demo debug] showInDocBar skipped", {
              hasBar: !!inDocBar,
              hasInput: !!inDocInput,
            });
          } catch (_) {}
        }
        return;
      }
      const focus =
        options && Object.prototype.hasOwnProperty.call(options, "focus")
          ? !!options.focus
          : true;
      const select =
        options && Object.prototype.hasOwnProperty.call(options, "select")
          ? !!options.select
          : focus;
      inDocBar.style.display = "flex";
      const liveBar = document.querySelector(".indoc-search-bar");
      if (liveBar && liveBar !== inDocBar) {
        liveBar.style.display = "flex";
      }
      if (typeof preset !== "undefined") {
        inDocInput.value = preset;
      }
      const presetValue =
        preset !== undefined && preset !== null
          ? String(preset)
          : String(inDocInput.value || "");
      InDocSearch.buildMatches(presetValue.trim());

      const contentEl = transcriptElements && transcriptElements.content;
      const q = presetValue.trim();
      if (
        contentEl &&
        q &&
        typeof contentEl.innerHTML === "string" &&
        contentEl.innerHTML.indexOf("indoc-highlight") === -1
      ) {
        const text = getCurrentTranscriptContent() || "";
        const lowerText = text.toLowerCase();
        const lowerNeedle = q.toLowerCase();
        const idx = lowerText.indexOf(lowerNeedle);
        if (idx !== -1) {
          const before = escapeHtml(text.slice(0, idx));
          const match = escapeHtml(text.slice(idx, idx + q.length));
          const after = escapeHtml(text.slice(idx + q.length));
          contentEl.innerHTML =
            before +
            '<mark class="indoc-highlight active">' +
            match +
            "</mark>" +
            after;
        }
      }
      if (focus) {
        setTimeout(() => {
          if (typeof inDocInput.focus === "function") {
            inDocInput.focus();
          }
          if (select && typeof inDocInput.select === "function") {
            inDocInput.select();
          } else if (
            !select &&
            typeof inDocInput.setSelectionRange === "function"
          ) {
            const length = inDocInput.value.length;
            inDocInput.setSelectionRange(length, length);
          }
        }, 0);
      }
    }

    function hideInDocBar() {
      if (!inDocBar) return;
      if (inDocInput) {
        inDocInput.value = "";
        InDocSearch.buildMatches("");
        inDocInput.blur();
      }
      // hide the bar and remove padding class
      inDocBar.style.display = "none";
      const body = document.querySelector(".transcript-view__body");
      if (body) body.classList.remove("indoc-active");
    }

    function presetInDocSearch(term, options = {}) {
      const normalized = (term || "").trim();
      if (!normalized) {
        hideInDocBar();
        return;
      }
      const focus =
        options && Object.prototype.hasOwnProperty.call(options, "focus")
          ? !!options.focus
          : false;
      const select =
        options && Object.prototype.hasOwnProperty.call(options, "select")
          ? !!options.select
          : focus;
      showInDocBar(normalized, { focus, select });
    }

    function handleDocumentKeydown(e) {
      const target = e.target;
      const typing = isTypingTarget(target);

      if (
        !typing &&
        isTranscriptActive() &&
        (e.key === "n" || e.key === "N") &&
        InDocSearch.hasQuery()
      ) {
        e.preventDefault();
        if (e.key === "n") InDocSearch.next();
        else InDocSearch.prev();
        return;
      }

      if (
        !typing &&
        e.key === "/" &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey &&
        !e.shiftKey
      ) {
        e.preventDefault();
        if (isLibraryActive()) {
          const input = getLibraryInput();
          if (!input) return;
          if (document.activeElement === input) {
            input.value = "";
            filterFiles("");
          } else {
            focusLibrarySearch(true);
          }
        } else if (isTranscriptActive()) {
          const visible = !!(inDocBar && inDocBar.style.display !== "none");
          if (visible && document.activeElement === inDocInput) {
            inDocInput.value = "";
            InDocSearch.buildMatches("");
          } else {
            showInDocBar();
          }
        }
        return;
      }

      if (isLibraryActive()) {
        if (
          (e.ctrlKey || e.metaKey) &&
          !e.altKey &&
          !e.shiftKey &&
          (e.key === "p" || e.key === "P")
        ) {
          e.preventDefault();
          setSearchMode("files");
          if (!typing) focusLibrarySearch();
          return;
        }
        if (
          (e.ctrlKey || e.metaKey) &&
          e.shiftKey &&
          !e.altKey &&
          (e.key === "f" || e.key === "F")
        ) {
          e.preventDefault();
          setSearchMode("content");
          if (!typing) focusLibrarySearch();
          return;
        }
      }
    }

    function handleLibraryInputKeydown(e) {
      if (e.key === "Tab") {
        e.preventDefault();
        cycleMode(e.shiftKey);
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        if (
          getSearchMode() === "content" &&
          getSearchTerm() &&
          getSearchTerm().length >= contentSearchMinLength
        ) {
          const first = Array.from(contentHits.entries()).sort(
            (a, b) => a[1].index - b[1].index,
          )[0];
          if (first) {
            const filename = first[0];
            maybeQueueInDocSearchPreset(filename);
            viewTranscript(filename);
            return;
          }
        }
        openFirstVisibleFileRow();
        return;
      }
      if (e.key === "Escape") {
        e.stopPropagation();
        e.target.blur();
      }
    }

    function onLibraryChipsClick() {
      if (isLibraryActive()) focusLibrarySearch(false);
    }

    function bindLibraryHandlers() {
      const input = getLibraryInput();
      if (input && !input.__demoHotkeysBound) {
        input.addEventListener("keydown", handleLibraryInputKeydown);
        input.__demoHotkeysBound = true;
      }
      const chips = getLibraryChips();
      if (chips && !chips.__demoHotkeysBound) {
        chips.addEventListener("click", onLibraryChipsClick);
        chips.__demoHotkeysBound = true;
      }
    }

    document.addEventListener("keydown", handleDocumentKeydown, true);
    bindLibraryHandlers();
    document.addEventListener("demo:partials-ready", bindLibraryHandlers);

    return { ensureInDocBar, showInDocBar, presetInDocSearch };
  })();

  return { hotkeys: SearchHotkeys };
}
